import { useState, useRef, useCallback, useEffect } from 'react'
import { API_URL } from '../config/api'
import './StickerConfigurator.css'

export default function StickerConfigurator({ product, onAdd, onClose }) {
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [sizes, setSizes] = useState([])
  const [selectedSize, setSelectedSize] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [dragging, setDragging] = useState(false)
  const fileRef = useRef()

  useEffect(() => {
    fetch(`${API_URL}/api/sticker-sizes?sort=order:asc`)
      .then(r => r.json())
      .then(d => {
        const list = (d.data || []).map(s => {
          const attrs = s.attributes || s
          return { id: s.id, label: attrs.label, dimensions: attrs.dimensions, price: Number(attrs.price) }
        })
        setSizes(list)
        setSelectedSize(list[Math.min(1, list.length - 1)] || null)
      })
      .catch(() => setSizes([]))
  }, [])

  const loadFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return
    setImageFile(file)
    const url = URL.createObjectURL(file)
    setImagePreview(url)
  }

  const handleFileChange = (e) => loadFile(e.target.files[0])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragging(false)
    loadFile(e.dataTransfer.files[0])
  }, [])

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true) }
  const handleDragLeave = () => setDragging(false)

  const handleAdd = () => {
    if (!imageFile) return
    onAdd({
      product,
      quantity,
      customization: {
        size: selectedSize,
        imageFile,
        imagePreview,
        imageName: imageFile.name,
      },
    })
    onClose()
  }

  const total = ((selectedSize?.price || 0) * quantity).toFixed(2)

  return (
    <div className="sc-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sc-modal">

        {/* Header */}
        <div className="sc-header">
          <div>
            <h2>Sticker personaliseren</h2>
            <p>{product.attributes.name}</p>
          </div>
          <button className="sc-close" onClick={onClose}>✕</button>
        </div>

        <div className="sc-body">

          {/* Left — controls */}
          <div className="sc-controls">

            {/* Upload */}
            <section className="sc-section">
              <h3>1. Upload jouw afbeelding</h3>
              <div
                className={`sc-dropzone${dragging ? ' sc-dropzone--drag' : ''}`}
                onClick={() => fileRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {imagePreview ? (
                  <div className="sc-dropzone-replace">
                    <img src={imagePreview} alt="Geüpload" />
                    <span>Klik om te vervangen</span>
                  </div>
                ) : (
                  <>
                    <div className="sc-upload-icon">📁</div>
                    <p>Sleep je afbeelding hierheen</p>
                    <span>of klik om te bladeren</span>
                    <small>PNG, JPG, WebP — max 10 MB<br />Transparante PNG geeft het beste die-cut effect</small>
                  </>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>
            </section>

            {/* Size */}
            <section className="sc-section">
              <h3>2. Kies een formaat</h3>
              <div className="sc-sizes">
                {sizes.map(size => (
                  <button
                    key={size.id}
                    className={`sc-size-btn${selectedSize?.id === size.id ? ' sc-size-btn--active' : ''}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    <span className="sc-size-label">{size.label}</span>
                    <span className="sc-size-dim">{size.dimensions}</span>
                    <span className="sc-size-price">€{size.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Quantity */}
            <section className="sc-section">
              <h3>3. Aantal</h3>
              <div className="sc-qty">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)}>+</button>
              </div>
            </section>

            {/* CTA */}
            <div className="sc-cta">
              <div className="sc-total">
                <span>Totaal</span>
                <strong>€{total}</strong>
              </div>
              <button
                className="sc-add-btn"
                onClick={handleAdd}
                disabled={!imageFile}
              >
                {imageFile ? '🛒 Toevoegen aan winkelwagen' : 'Upload eerst een afbeelding'}
              </button>
            </div>
          </div>

          {/* Right — preview */}
          <div className="sc-preview-panel">
            <h3>Voorbeeld</h3>
            <div className="sc-preview-stage">
              {imagePreview ? (
                <div className="sc-sticker-wrap">
                  <img
                    src={imagePreview}
                    alt="Sticker preview"
                    className="sc-sticker-img"
                  />
                  <div className="sc-sticker-gloss" />
                </div>
              ) : (
                <div className="sc-preview-placeholder">
                  <span>👆</span>
                  <p>Upload een afbeelding om het voorbeeld te zien</p>
                </div>
              )}
            </div>
            {imagePreview && selectedSize && (
              <p className="sc-preview-info">
                Die-cut • {selectedSize.dimensions} • {quantity} st.
              </p>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
